import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { type ServiceVendorDto, type ServiceVendorId, createServiceVendorId } from '@/services/api/petseo.client.wrapper';

interface ProfileState {
  selectedProfileId: ServiceVendorId | null;
  defaultProfileId: ServiceVendorId | null;
  profiles: ServiceVendorDto[];
  error: string | null;
  isInitialized: boolean;
}

interface ProfileActions {
  setSelectedProfileId: (id: ServiceVendorId | null) => void;
  setDefaultProfileId: (id: ServiceVendorId | null) => void;
  setProfiles: (profiles: ServiceVendorDto[]) => void;
  setError: (error: string | null) => void;
  initializeSelection: () => void;
  validateSelection: () => void;
  reset: () => void;
}

const initialState: ProfileState = {
  selectedProfileId: null,
  defaultProfileId: null,
  profiles: [],
  error: null,
  isInitialized: false,
};

export const useProfileStore = create<ProfileState & ProfileActions>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        ...initialState,

        setSelectedProfileId: (id: ServiceVendorId | null) => set({ selectedProfileId: id }),

        setDefaultProfileId: (id: ServiceVendorId | null) => {
          const { defaultProfileId } = get();
          if (defaultProfileId && String(defaultProfileId) === String(id)) {
            set({ defaultProfileId: null });
          } else {
            set({ defaultProfileId: id });
          }
        },

        setProfiles: (profiles: ServiceVendorDto[]) => set({ profiles }),

        setError: (error: string | null) => set({ error }),

        initializeSelection: () => {
          const { profiles, selectedProfileId, defaultProfileId, isInitialized } = get();
          if (isInitialized && selectedProfileId) return;

          const defaultProfile = defaultProfileId ? profiles.find((p) => p.id === String(defaultProfileId)) : null;
          const target = defaultProfile || profiles[0];
          if (target?.id) {
            set({ selectedProfileId: createServiceVendorId(target.id), isInitialized: true });
          } else {
            set({ isInitialized: true });
          }
        },

        validateSelection: () => {
          const { profiles, selectedProfileId } = get();
          if (!selectedProfileId) return;

          const exists = profiles.some((p) => p.id === String(selectedProfileId));
          if (exists) return;

          const first = profiles[0];
          set({ selectedProfileId: first?.id ? createServiceVendorId(first.id) : null });
        },

        reset: () => set(initialState),
      }),
      {
        name: 'petseo-b2b-profile',
        partialize: (state) => ({ selectedProfileId: state.selectedProfileId, defaultProfileId: state.defaultProfileId }),
      }
    )
  )
);

export const useSelectedProfileId = () => useProfileStore((s) => s.selectedProfileId);
export const useProfiles = () => useProfileStore((s) => s.profiles);
export const useProfileError = () => useProfileStore((s) => s.error);
export const useHasProfiles = () => useProfileStore((s) => s.profiles.length > 0);
export const useIsProfileInitialized = () => useProfileStore((s) => s.isInitialized);

export const useDefaultProfileId = () => useProfileStore((s) => s.defaultProfileId);

export const useSelectedProfile = () => useProfileStore((s) => s.profiles.find((p) => p.id === s.selectedProfileId));
